// Opt-in teaching presentation only. The authored Markdown remains the text source.
import { ROLES, isRecord } from './model.js';
export function bodyFingerprint(body) {
    let h = 2166136261;
    for (const c of String(body).replace(/\r\n/g, '\n')) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
    // Accidental-edit guard, not a security hash or authorization check.
    return 'fnv1a-' + (h >>> 0).toString(16).padStart(8, '0');
}
export function parsePatternBody(body) {
    if (typeof body !== 'string') return null;
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    const start = lines.findIndex(l => l.startsWith('|'));
    if (start < 0 || !/^\|(?:\s*:?-+:?\s*\|){4}$/.test(lines[start + 1] || '')) return null;
    let end = start + 2;
    while (lines[end]?.startsWith('|')) end++;
    const cells = line => line.slice(1, -1).split('|').map(s => s.trim());
    const headers = cells(lines[start]);
    if (headers.join('|') !== 'Pattern and grammatical status|How to identify it|Original example|What to check' || end - start !== 8) return null;
    const rows = lines.slice(start + 2, end).map((line, i) => {
        const c = cells(line), status = c[0]?.match(/^\*\*([✓✕] (?:GRAMMATICAL|ERROR) — [^*]+)\*\* — (.+)$/);
        const example = c[2]?.match(/^“([^”]+)”$/);
        return c.length === 4 && status && example ? { id: 'r' + (i + 1), label: status[1], name: status[2], identify: c[1], example: example[1], check: c[3] } : null;
    });
    if (rows.some(r => !r)) return null;
    const suffix = lines.slice(end).join('\n');
    const context = suffix.match(/^\s*\*\*Context note — fragments\*\*\s*\n\n((?:- .+\n?){4})([\s\S]*)$/);
    if (!context) return null;
    return { prefix: lines.slice(0, start).join('\n'), rows, context: '**Context note — fragments**\n\n' + context[1], suffix: context[2], headers };
}
const EXAMPLES = ['The room opens at four.', 'Keep the doorway clear.', 'Because the library closes early.', 'A quiet place for students.', 'The room is available, the trial could begin.', 'The room is available the trial could begin.'];
export function createPatternCards(body) {
    const parsed = parsePatternBody(body);
    if (!parsed || parsed.rows.some((r, i) => r.example !== EXAMPLES[i])) return null;
    return { version: 1, bodyFingerprint: bodyFingerprint(body), rows: parsed.rows.map((r, i) => ({ id: r.id, example: r.example, status: i < 2 ? 'grammatical' : 'error' })), marks: [
        { id: 'subject', rowId: 'r1', kind: 'span', anchor: 'The room', role: 'subject', label: 'Subject' },
        { id: 'finite', rowId: 'r1', kind: 'span', anchor: 'opens', role: 'verb', label: 'Finite verb' },
        { id: 'instruction', rowId: 'r2', kind: 'span', anchor: 'Keep', role: 'verb', label: 'Instruction verb; understood subject: you' },
        { id: 'clause_missing', rowId: 'r3', kind: 'missing', anchor: '.', label: 'Missing main clause: the reason is unfinished' },
        { id: 'verb_missing', rowId: 'r4', kind: 'missing', anchor: '.', label: 'Missing main-clause verb and complete claim' },
        { id: 'clause_a', rowId: 'r5', kind: 'clause', anchor: 'The room is available', label: 'Main clause 1' },
        { id: 'comma', rowId: 'r5', kind: 'error', anchor: ',', label: 'Faulty join: comma alone' },
        { id: 'clause_b', rowId: 'r5', kind: 'clause', anchor: 'the trial could begin', label: 'Main clause 2' },
        { id: 'boundary', rowId: 'r6', kind: 'gap', anchor: 'available', label: 'Missing boundary before “the trial”' },
    ] };
}
export function patternCardErrors(body, value) {
    if (value === undefined) return [];
    if (!isRecord(value) || value.version !== 1) return ['Pattern cards require version 1.'];
    const p = parsePatternBody(body);
    if (!p || value.bodyFingerprint !== bodyFingerprint(body)) return ['Teaching text changed or is not the supported six-row table. Review and refresh the card settings; original text remains available.'];
    if (!Array.isArray(value.rows) || value.rows.length !== 6 || !Array.isArray(value.marks) || value.marks.length > 24) return ['Pattern cards need six rows and at most 24 marks.'];
    const errors = [], ids = new Set();
    const scalarRow = row => isRecord(row) && Object.values(row).every(v => typeof v === 'string' && v.length <= 2000);
    for (const [i, row] of value.rows.entries()) {
        const source = p.rows[i];
        if (!scalarRow(row) || row.id !== source.id || row.example !== source.example || !['grammatical', 'error'].includes(row.status)
            || !source.label.startsWith(row.status === 'error' ? '✕ ERROR' : '✓ GRAMMATICAL')) errors.push('Row ' + (i + 1) + ': status and example must match the authored teaching row.');
    }
    const occupied = new Map();
    for (const m of value.marks) {
        if (!scalarRow(m) || !/^[a-zA-Z0-9_-]{1,64}$/.test(m.id || '') || ids.has(m.id)) { errors.push('Invalid or duplicate marker ID.'); continue; }
        ids.add(m.id);
        const row = p.rows.find(r => r.id === m.rowId);
        if (!row || !m.label?.trim() || !m.anchor || !['span', 'error', 'clause', 'gap', 'missing'].includes(m.kind)) { errors.push('Each marker needs a row, kind, anchor and explanation.'); continue; }
        const at = row.example.indexOf(m.anchor);
        if (at < 0 || row.example.indexOf(m.anchor, at + 1) >= 0) errors.push('Marker anchor must occur exactly once in its original sentence.');
        if (m.kind === 'span' && !Object.hasOwn(ROLES, m.role)) errors.push('Identification marks need a supported grammar role.');
        if (m.kind === 'error' && value.rows.find(r => r.id === m.rowId)?.status !== 'error') errors.push('Error marks require an explicit error card.');
        if (['span', 'error', 'clause'].includes(m.kind)) {
            const ranges = occupied.get(m.rowId) || [];
            if (ranges.some(([a, b]) => at < b && at + m.anchor.length > a)) errors.push('Sentence marks must not overlap.');
            ranges.push([at, at + m.anchor.length]); occupied.set(m.rowId, ranges);
        }
    }
    return [...new Set(errors)];
}
export function patternCardView(body, value) {
    return value !== undefined && !patternCardErrors(body, value).length ? parsePatternBody(body) : null;
}
export function sectionPatternCardErrors(content) {
    if (!['technique', 'analysis'].includes(content?.sectionKind)) return [];
    return (Array.isArray(content?.blocks?.parts) ? content.blocks.parts : []).flatMap((p, i) => patternCardErrors(p?.body, p?.patternCards).map(e => 'Content section ' + (i + 1) + ': ' + e));
}
